/**
 * Share a whole number of days (or sessions, or pages) out by weight.
 *
 * Rounding each share on its own is how a 22-day plan ends up with 23 days in it. This
 * is the largest-remainder (Hamilton) method instead: everyone gets the whole part of
 * their exact share, and the units left over go to the largest fractional parts — so the
 * allocations always sum to `total`, and no share is ever more than one unit away from
 * its exact value.
 *
 * Remainders are compared as `weight × total − whole × sum`, which is exact for integer
 * weights, so two steps with the same share always tie rather than being split by a
 * floating-point last digit. Ties go to the larger weight, then to the earlier position,
 * so the same inputs always give the same plan.
 */
export function largestRemainder(
  weights: readonly number[],
  total: number
): number[] {
  const clean = weights.map((weight) => Math.max(0, weight));
  const sum = clean.reduce((acc, weight) => acc + weight, 0);
  if (sum <= 0 || total <= 0) {
    return clean.map(() => 0);
  }
  const rows = clean.map((weight, index) => {
    const scaled = weight * total;
    const whole = Math.floor(scaled / sum);
    return { index, weight, whole, remainder: scaled - whole * sum };
  });
  const allocation = rows.map((row) => row.whole);
  let remaining = total - allocation.reduce((acc, value) => acc + value, 0);
  const order = [...rows].sort(
    (a, b) =>
      b.remainder - a.remainder || b.weight - a.weight || a.index - b.index
  );
  for (const row of order) {
    if (remaining <= 0) {
      break;
    }
    allocation[row.index] += 1;
    remaining -= 1;
  }
  return allocation;
}
